import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, Edit, Trash2, Eye, Star, StarOff, 
  Save, X, Search, Filter, RefreshCw, Image as ImageIcon,
  Upload, Video, Link as LinkIcon
} from 'lucide-react';
import { blogAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { useToast } from '../../hooks/use-toast';
import AdminLayout from './AdminLayout';

const AdminBlog = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showArticleModal, setShowArticleModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [activeTab, setActiveTab] = useState('articles');
  const [searchTerm, setSearchTerm] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const contentFileInputRef = useRef(null);
  const { toast } = useToast();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [articlesRes, categoriesRes] = await Promise.all([
        blogAPI.getArticles({ published_only: false }),
        blogAPI.getCategories()
      ]);
      setArticles(articlesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de charger les données', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Upload image function
  const handleImageUpload = async (file, isContentImage = false) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast({ title: 'Erreur', description: 'Format d\'image non supporté. Utilisez JPG, PNG, WebP ou GIF.', variant: 'destructive' });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Erreur', description: 'L\'image ne doit pas dépasser 5 Mo.', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload/image`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) throw new Error('Upload failed');
      
      const data = await response.json();
      // Convert relative URL to absolute URL
      const imageUrl = data.url.startsWith('/') 
        ? `${process.env.REACT_APP_BACKEND_URL}${data.url}`
        : data.url;

      if (isContentImage) {
        // Insert image into content
        const imageTag = `\n<img src="${imageUrl}" alt="${file.name}" style="max-width: 100%; height: auto; margin: 1rem 0;" />\n`;
        setEditingArticle(prev => ({
          ...prev,
          content: (prev.content || '') + imageTag
        }));
        toast({ title: 'Image ajoutée au contenu' });
      } else {
        // Set as featured image
        setEditingArticle(prev => ({
          ...prev,
          image_url: imageUrl
        }));
        toast({ title: 'Image de mise en avant définie' });
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({ title: 'Erreur', description: 'Impossible d\'uploader l\'image', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  // Insert video URL into content
  const handleInsertVideo = () => {
    const videoUrl = prompt('Entrez l\'URL de la vidéo YouTube ou Vimeo:');
    if (!videoUrl) return;

    let embedCode = '';
    
    // YouTube
    if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
      const videoId = videoUrl.includes('youtu.be') 
        ? videoUrl.split('/').pop() 
        : new URL(videoUrl).searchParams.get('v');
      if (videoId) {
        embedCode = `\n<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; margin: 1rem 0;">
  <iframe src="https://www.youtube.com/embed/${videoId}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" frameborder="0" allowfullscreen></iframe>
</div>\n`;
      }
    }
    // Vimeo
    else if (videoUrl.includes('vimeo.com')) {
      const videoId = videoUrl.split('/').pop();
      if (videoId) {
        embedCode = `\n<div style="position: relative; padding-bottom: 56.25%; height: 0; overflow: hidden; margin: 1rem 0;">
  <iframe src="https://player.vimeo.com/video/${videoId}" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%;" frameborder="0" allowfullscreen></iframe>
</div>\n`;
      }
    }

    if (embedCode) {
      setEditingArticle(prev => ({
        ...prev,
        content: (prev.content || '') + embedCode
      }));
      toast({ title: 'Vidéo ajoutée au contenu' });
    } else {
      toast({ title: 'Erreur', description: 'URL de vidéo non reconnue', variant: 'destructive' });
    }
  };

  // Insert link into content
  const handleInsertLink = () => {
    const linkUrl = prompt('Entrez l\'URL du lien:');
    if (!linkUrl) return;
    const linkText = prompt('Texte du lien:', linkUrl);
    if (!linkText) return;

    const linkTag = `<a href="${linkUrl}" target="_blank" rel="noopener noreferrer" style="color: #285BBB;">${linkText}</a>`;
    setEditingArticle(prev => ({
      ...prev,
      content: (prev.content || '') + linkTag
    }));
    toast({ title: 'Lien ajouté' });
  };

  // Article functions
  const handleSaveArticle = async () => {
    if (!editingArticle.title) {
      toast({ title: 'Erreur', description: 'Le titre est obligatoire', variant: 'destructive' });
      return;
    }

    try {
      if (editingArticle.id) {
        await blogAPI.updateArticle(editingArticle.id, editingArticle);
        toast({ title: 'Article mis à jour' });
      } else {
        await blogAPI.createArticle(editingArticle);
        toast({ title: 'Article créé' });
      }
      setShowArticleModal(false);
      setEditingArticle(null);
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', description: 'Impossible de sauvegarder', variant: 'destructive' });
    }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    try {
      await blogAPI.deleteArticle(id);
      toast({ title: 'Article supprimé' });
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      await blogAPI.toggleFeatured(id);
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  // Category functions
  const handleSaveCategory = async () => {
    if (!editingCategory.name) {
      toast({ title: 'Erreur', description: 'Le nom est obligatoire', variant: 'destructive' });
      return;
    }

    try {
      if (editingCategory.id) {
        await blogAPI.updateCategory(editingCategory.id, editingCategory);
        toast({ title: 'Catégorie mise à jour' });
      } else {
        await blogAPI.createCategory(editingCategory);
        toast({ title: 'Catégorie créée' });
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Supprimer cette catégorie ?')) return;
    try {
      await blogAPI.deleteCategory(id);
      toast({ title: 'Catégorie supprimée' });
      loadData();
    } catch (error) {
      toast({ title: 'Erreur', variant: 'destructive' });
    }
  };

  const filteredArticles = articles.filter(a => 
    searchTerm === '' || 
    a.title.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openNewArticle = () => {
    setEditingArticle({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      image_url: '',
      category_id: categories[0]?.id || '',
      author: 'The English Speaking Center',
      is_featured: false,
      is_published: false,
      tags: []
    });
    setShowArticleModal(true);
  };

  const openEditArticle = (article) => {
    setEditingArticle({ ...article });
    setShowArticleModal(true);
  };

  const openNewCategory = () => {
    setEditingCategory({
      name: '',
      slug: '',
      description: '',
      color: '#285BBB',
      order: categories.length + 1
    });
    setShowCategoryModal(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory({ ...category });
    setShowCategoryModal(true);
  };

  if (loading) {
    return (
      <AdminLayout title="Blog">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-8 w-8 animate-spin text-[#285BBB]" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gestion du Blog" subtitle="Créez et gérez vos articles de blog">
      {/* Tabs */}
      <div className="flex gap-4 mb-6">
        <button
          onClick={() => setActiveTab('articles')}
          className={`px-4 py-2 font-semibold rounded-lg transition-colors ${
            activeTab === 'articles' ? 'bg-[#285BBB] text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          Articles ({articles.length})
        </button>
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-4 py-2 font-semibold rounded-lg transition-colors ${
            activeTab === 'categories' ? 'bg-[#285BBB] text-white' : 'bg-gray-200 text-gray-700'
          }`}
        >
          Catégories ({categories.length})
        </button>
      </div>

      {/* Articles Tab */}
      {activeTab === 'articles' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Rechercher un article..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button onClick={openNewArticle} className="bg-[#285BBB] hover:bg-[#1e4a9a]">
              <Plus className="h-5 w-5 mr-2" />
              Nouvel article
            </Button>
          </div>

          {/* Articles List */}
          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Article</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Catégorie</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">À la une</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Publié</th>
                  <th className="px-6 py-3 text-center text-xs font-semibold text-gray-600 uppercase">Vues</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredArticles.map((article) => (
                  <tr key={article.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {article.image_url ? (
                          <img src={article.image_url} alt="" className="w-12 h-12 rounded object-cover" />
                        ) : (
                          <div className="w-12 h-12 rounded bg-gray-200 flex items-center justify-center">
                            <ImageIcon className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-[#2C3C65]">{article.title}</p>
                          <p className="text-sm text-gray-500 line-clamp-1">{article.excerpt}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span 
                        className="px-2 py-1 text-xs font-semibold rounded-full text-white"
                        style={{ backgroundColor: categories.find(c => c.id === article.category_id)?.color || '#285BBB' }}
                      >
                        {article.category_name || 'Non classé'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => handleToggleFeatured(article.id)}
                        className={`p-1 rounded ${article.is_featured ? 'text-yellow-500' : 'text-gray-300 hover:text-yellow-400'}`}
                      >
                        {article.is_featured ? <Star className="h-5 w-5 fill-current" /> : <StarOff className="h-5 w-5" />}
                      </button>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        article.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {article.is_published ? 'Oui' : 'Brouillon'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center text-gray-500">
                      {article.views || 0}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <a
                          href={`/blog/${article.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 text-gray-500 hover:text-[#285BBB]"
                        >
                          <Eye className="h-5 w-5" />
                        </a>
                        <button
                          onClick={() => openEditArticle(article)}
                          className="p-2 text-gray-500 hover:text-[#285BBB]"
                        >
                          <Edit className="h-5 w-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteArticle(article.id)}
                          className="p-2 text-gray-500 hover:text-red-500"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredArticles.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                Aucun article trouvé
              </div>
            )}
          </div>
        </div>
      )}

      {/* Categories Tab */}
      {activeTab === 'categories' && (
        <div className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={openNewCategory} className="bg-[#285BBB] hover:bg-[#1e4a9a]">
              <Plus className="h-5 w-5 mr-2" />
              Nouvelle catégorie
            </Button>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white rounded-xl shadow-md p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div 
                    className="w-4 h-4 rounded-full"
                    style={{ backgroundColor: category.color }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => openEditCategory(category)}
                      className="p-1 text-gray-500 hover:text-[#285BBB]"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteCategory(category.id)}
                      className="p-1 text-gray-500 hover:text-red-500"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
                <h3 className="font-bold text-[#2C3C65]">{category.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{category.description}</p>
                <p className="text-xs text-gray-400 mt-2">Slug: {category.slug}</p>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Article Modal */}
      {showArticleModal && editingArticle && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
          >
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#2C3C65]">
                {editingArticle.id ? 'Modifier l\'article' : 'Nouvel article'}
              </h2>
              <button onClick={() => setShowArticleModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-2">Titre *</label>
                  <Input
                    value={editingArticle.title}
                    onChange={(e) => setEditingArticle({ ...editingArticle, title: e.target.value })}
                    placeholder="Titre de l'article"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-2">Slug (URL)</label>
                  <Input
                    value={editingArticle.slug}
                    onChange={(e) => setEditingArticle({ ...editingArticle, slug: e.target.value })}
                    placeholder="Auto-généré si vide"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Résumé</label>
                <Textarea
                  value={editingArticle.excerpt}
                  onChange={(e) => setEditingArticle({ ...editingArticle, excerpt: e.target.value })}
                  placeholder="Court résumé de l'article"
                  rows={2}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Image de mise en avant</label>
                <div className="flex flex-col gap-3">
                  <div className="flex gap-2">
                    <Input
                      value={editingArticle.image_url}
                      onChange={(e) => setEditingArticle({ ...editingArticle, image_url: e.target.value })}
                      placeholder="URL de l'image ou cliquez sur Parcourir"
                      className="flex-1"
                    />
                    <input
                      type="file"
                      ref={fileInputRef}
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], false)}
                      className="hidden"
                    />
                    <Button 
                      type="button"
                      variant="outline" 
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Upload...' : 'Parcourir'}
                    </Button>
                  </div>
                  {editingArticle.image_url && (
                    <div className="relative">
                      <img 
                        src={editingArticle.image_url} 
                        alt="Aperçu" 
                        className="h-32 object-cover rounded-lg border"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <button
                        type="button"
                        onClick={() => setEditingArticle({ ...editingArticle, image_url: '' })}
                        className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-[#2C3C65]">Contenu de l'article</label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      ref={contentFileInputRef}
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e.target.files[0], true)}
                      className="hidden"
                    />
                    <Button 
                      type="button"
                      size="sm"
                      variant="outline" 
                      onClick={() => contentFileInputRef.current?.click()}
                      disabled={uploading}
                    >
                      <ImageIcon className="h-4 w-4 mr-1" />
                      Image
                    </Button>
                    <Button 
                      type="button"
                      size="sm"
                      variant="outline" 
                      onClick={handleInsertVideo}
                    >
                      <Video className="h-4 w-4 mr-1" />
                      Vidéo
                    </Button>
                    <Button 
                      type="button"
                      size="sm"
                      variant="outline" 
                      onClick={handleInsertLink}
                    >
                      <LinkIcon className="h-4 w-4 mr-1" />
                      Lien
                    </Button>
                  </div>
                </div>
                <Textarea
                  value={editingArticle.content}
                  onChange={(e) => setEditingArticle({ ...editingArticle, content: e.target.value })}
                  placeholder="<p>Contenu de l'article...</p>&#10;&#10;Utilisez les boutons ci-dessus pour ajouter des images et vidéos."
                  rows={12}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Vous pouvez utiliser du HTML. Les images et vidéos seront insérées automatiquement.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-2">Catégorie</label>
                  <select
                    value={editingArticle.category_id}
                    onChange={(e) => setEditingArticle({ ...editingArticle, category_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">Non classé</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-2">Auteur</label>
                  <Input
                    value={editingArticle.author}
                    onChange={(e) => setEditingArticle({ ...editingArticle, author: e.target.value })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#2C3C65] mb-2">Tags (séparés par des virgules)</label>
                  <Input
                    value={(editingArticle.tags || []).join(', ')}
                    onChange={(e) => setEditingArticle({ 
                      ...editingArticle, 
                      tags: e.target.value.split(',').map(t => t.trim()).filter(Boolean) 
                    })}
                    placeholder="tag1, tag2, tag3"
                  />
                </div>
              </div>

              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingArticle.is_published}
                    onChange={(e) => setEditingArticle({ ...editingArticle, is_published: e.target.checked })}
                    className="h-5 w-5 text-[#285BBB] rounded"
                  />
                  <span className="text-[#2C3C65]">Publié</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={editingArticle.is_featured}
                    onChange={(e) => setEditingArticle({ ...editingArticle, is_featured: e.target.checked })}
                    className="h-5 w-5 text-yellow-500 rounded"
                  />
                  <span className="text-[#2C3C65]">Mis en avant (homepage)</span>
                </label>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t px-6 py-4 flex justify-end gap-4">
              <Button variant="outline" onClick={() => setShowArticleModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveArticle} className="bg-[#285BBB] hover:bg-[#1e4a9a]">
                <Save className="h-5 w-5 mr-2" />
                Sauvegarder
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Category Modal */}
      {showCategoryModal && editingCategory && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-xl w-full max-w-md"
          >
            <div className="border-b px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-[#2C3C65]">
                {editingCategory.id ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
              </h2>
              <button onClick={() => setShowCategoryModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Nom *</label>
                <Input
                  value={editingCategory.name}
                  onChange={(e) => setEditingCategory({ ...editingCategory, name: e.target.value })}
                  placeholder="Nom de la catégorie"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Slug</label>
                <Input
                  value={editingCategory.slug}
                  onChange={(e) => setEditingCategory({ ...editingCategory, slug: e.target.value })}
                  placeholder="Auto-généré si vide"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Description</label>
                <Textarea
                  value={editingCategory.description}
                  onChange={(e) => setEditingCategory({ ...editingCategory, description: e.target.value })}
                  rows={2}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#2C3C65] mb-2">Couleur</label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={editingCategory.color}
                    onChange={(e) => setEditingCategory({ ...editingCategory, color: e.target.value })}
                    className="h-10 w-20 rounded cursor-pointer"
                  />
                  <Input
                    value={editingCategory.color}
                    onChange={(e) => setEditingCategory({ ...editingCategory, color: e.target.value })}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="border-t px-6 py-4 flex justify-end gap-4">
              <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveCategory} className="bg-[#285BBB] hover:bg-[#1e4a9a]">
                <Save className="h-5 w-5 mr-2" />
                Sauvegarder
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminBlog;
